<template>
  <section class="hs" aria-label="Statistiche">
    <!-- [sc-48951] Niente titolo: il saluto sopra ha già aperto la pagina, e
         «Come va» sopra dei numeri che si leggono da soli era un cartello su
         una porta aperta. Il nome resta per chi naviga a voce, in
         `aria-label`. -->
    <header class="hs-testa">
      <!-- [sc-48951] Sulla stessa riga della tendina: dice con cosa si
           confronta quello che la tendina sceglie, e due righe per una frase
           sola lasciavano una fascia vuota sotto il saluto. -->
      <p v-if="confrontoCon" class="hs-confronto">
        Le variazioni sono rispetto a <strong>{{ confrontoCon }}</strong
        >.
      </p>
      <div class="hs-periodo">
        <span class="hs-periodo__label">Periodo</span>
        <StatusSelect
          :modelValue="periodo"
          :options="opzioniPeriodo"
          :disabled="carico"
          placeholderIcon="fa-calendar"
          @update:modelValue="scegliPeriodo"
        />
      </div>
    </header>

    <p v-if="errore" class="hs-errore">{{ errore }}</p>
    <p v-else-if="carico" class="hs-nota">Carico i numeri…</p>

    <template v-else-if="dati">
      <div class="hs-griglia">
        <!-- Preventivi -->
        <article
          class="hs-card hs-card--apribile"
          :class="{ 'hs-card--aperta': aperta === 'preventivi' }"
          role="button"
          tabindex="0"
          :aria-expanded="aperta === 'preventivi'"
          @click="apri('preventivi')"
          @keydown.enter.prevent="apri('preventivi')"
          @keydown.space.prevent="apri('preventivi')"
        >
          <h3>
            <i class="fa-solid fa-file-invoice"></i> Preventivi
            <i class="fa-solid fa-chevron-down hs-freccia"></i>
          </h3>
          <p class="hs-numero">{{ dati.preventivi.numero }}</p>
          <p class="hs-sotto">
            {{ soldi(dati.preventivi.valore) }} di preventivi con data nel
            periodo
            <span
              v-if="dati.preventivi.variazione.valore !== null"
              class="hs-delta"
              :class="versoDi(dati.preventivi.variazione.valore)"
              :title="`Rispetto a ${confrontoCon}`"
            >
              {{ delta(dati.preventivi.variazione.valore) }}
            </span>
          </p>
          <dl class="hs-righe">
            <div>
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.medio"
                  >Valore medio
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ soldi(dati.preventivi.medio) }}</dd>
            </div>
            <div>
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.convertiti"
                  >Diventati ordini
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>
                {{ dati.preventivi.convertiti }}
                <span class="hs-muto"
                  >({{ dati.preventivi.conversione }}%)</span
                >
              </dd>
            </div>
            <div>
              <dt>
                <span
                  class="hs-def"
                  tabindex="0"
                  :title="DEF.giorniPerConvertire"
                  >Tempo per decidere
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ quantiGiorni(dati.preventivi.giorniPerConvertire) }}</dd>
            </div>
          </dl>
          <!-- La conversione è l'unica percentuale che conta davvero qui:
               vista come barra si legge senza leggerla. -->
          <div
            class="hs-conversione"
            :title="`${dati.preventivi.conversione}% dei preventivi è diventato un ordine`"
          >
            <span :style="{ width: dati.preventivi.conversione + '%' }"></span>
          </div>
          <div v-if="aperta === 'preventivi'" class="hs-torta" @click.stop>
            <p class="hs-torta__nota">{{ torte.preventivi.nota }}</p>
            <PieStat
              v-if="torte.preventivi.fette.length"
              :fette="torte.preventivi.fette"
              :formato="torte.preventivi.formato"
            />
            <p v-else class="hs-nota">Niente da dividere in questo periodo.</p>
          </div>
        </article>

        <!-- Ordini -->
        <article
          class="hs-card hs-card--apribile"
          :class="{ 'hs-card--aperta': aperta === 'ordini' }"
          role="button"
          tabindex="0"
          :aria-expanded="aperta === 'ordini'"
          @click="apri('ordini')"
          @keydown.enter.prevent="apri('ordini')"
          @keydown.space.prevent="apri('ordini')"
        >
          <h3>
            <i class="fa-solid fa-clipboard-list"></i> Ordini
            <i class="fa-solid fa-chevron-down hs-freccia"></i>
          </h3>
          <p class="hs-numero">{{ dati.ordini.confermati }}</p>
          <p class="hs-sotto">
            {{ soldi(dati.ordini.valore) }} confermati nel periodo
            <span
              v-if="dati.ordini.variazione.valore !== null"
              class="hs-delta"
              :class="versoDi(dati.ordini.variazione.valore)"
              :title="`Rispetto a ${confrontoCon}`"
            >
              {{ delta(dati.ordini.variazione.valore) }}
            </span>
          </p>
          <p class="hs-etichetta-gruppo">
            <span class="hs-def" tabindex="0" :title="DEF.daConsegnare">
              Da consegnare, oggi
              <i class="fa-regular fa-circle-question"></i>
            </span>
          </p>
          <ul class="hs-pastiglie">
            <li v-for="stato in dati.ordini.perStato" :key="stato.stato">
              {{ stato.stato }} <strong>{{ stato.quanti }}</strong>
            </li>
            <li v-if="dati.ordini.perStato.length === 0" class="hs-muto">
              Niente da consegnare
            </li>
          </ul>
          <dl class="hs-righe">
            <div
              class="hs-riga--forte"
              :class="{ 'hs-riga--allarme': dati.ordini.daIncassare > 0 }"
            >
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.daIncassare"
                  >Da incassare
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ soldi(dati.ordini.daIncassare) }}</dd>
            </div>
          </dl>
          <div v-if="aperta === 'ordini'" class="hs-torta" @click.stop>
            <p class="hs-torta__nota">{{ torte.ordini.nota }}</p>
            <PieStat
              v-if="torte.ordini.fette.length"
              :fette="torte.ordini.fette"
              :formato="torte.ordini.formato"
            />
            <p v-else class="hs-nota">Niente da dividere in questo periodo.</p>
          </div>
        </article>

        <!-- Clienti -->
        <article
          class="hs-card hs-card--apribile"
          :class="{ 'hs-card--aperta': aperta === 'clienti' }"
          role="button"
          tabindex="0"
          :aria-expanded="aperta === 'clienti'"
          @click="apri('clienti')"
          @keydown.enter.prevent="apri('clienti')"
          @keydown.space.prevent="apri('clienti')"
        >
          <h3>
            <i class="fa-solid fa-user-group"></i> Clienti
            <i class="fa-solid fa-chevron-down hs-freccia"></i>
          </h3>
          <p class="hs-numero">{{ dati.clienti.attivi }}</p>
          <p class="hs-sotto">
            con un preventivo nel periodo
            <span
              v-if="dati.clienti.variazione.attivi !== null"
              class="hs-delta"
              :class="versoDi(dati.clienti.variazione.attivi)"
              :title="`Rispetto a ${confrontoCon}`"
            >
              {{ delta(dati.clienti.variazione.attivi) }}
            </span>
          </p>
          <dl class="hs-righe">
            <div>
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.nuoviClienti"
                  >Nuovi in anagrafica
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ dati.clienti.nuovi }}</dd>
            </div>
            <div>
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.giaClienti"
                  >Già clienti da prima
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ dati.clienti.giaClienti }}</dd>
            </div>
            <div>
              <dt>
                <span
                  class="hs-def"
                  tabindex="0"
                  :title="DEF.valoreMedioCliente"
                  >Vale in media
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ soldi(dati.clienti.valoreMedioCliente) }}</dd>
            </div>
            <div>
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.ogniQuantiGiorni"
                  >Torna ogni
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ quantiGiorni(dati.clienti.ogniQuantiGiorni) }}</dd>
            </div>
          </dl>
          <div v-if="aperta === 'clienti'" class="hs-torta" @click.stop>
            <p class="hs-torta__nota">{{ torte.clienti.nota }}</p>
            <PieStat
              v-if="torte.clienti.fette.length"
              :fette="torte.clienti.fette"
              :formato="torte.clienti.formato"
            />
            <p v-else class="hs-nota">Niente da dividere in questo periodo.</p>
          </div>
        </article>

        <!-- Magazzino: una fotografia di adesso, senza periodo -->
        <article
          class="hs-card hs-card--apribile"
          :class="{ 'hs-card--aperta': aperta === 'magazzino' }"
          role="button"
          tabindex="0"
          :aria-expanded="aperta === 'magazzino'"
          @click="apri('magazzino')"
          @keydown.enter.prevent="apri('magazzino')"
          @keydown.space.prevent="apri('magazzino')"
        >
          <h3>
            <i class="fa-solid fa-warehouse"></i> Magazzino
            <i class="fa-solid fa-chevron-down hs-freccia"></i>
          </h3>
          <p class="hs-numero">{{ dati.magazzino.articoli }}</p>
          <p class="hs-sotto">articoli con giacenza, oggi</p>
          <dl class="hs-righe">
            <div
              :class="{ 'hs-riga--allarme': dati.magazzino.sottoScorta > 0 }"
            >
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.sottoScorta"
                  >Sotto scorta
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ dati.magazzino.sottoScorta }}</dd>
            </div>
            <div class="hs-riga--forte">
              <dt>
                <span class="hs-def" tabindex="0" :title="DEF.valoreGiacenza"
                  >Valore
                  <i class="fa-regular fa-circle-question"></i>
                </span>
              </dt>
              <dd>{{ soldi(dati.magazzino.valore) }}</dd>
            </div>
          </dl>
          <div v-if="aperta === 'magazzino'" class="hs-torta" @click.stop>
            <p class="hs-torta__nota">{{ torte.magazzino.nota }}</p>
            <PieStat
              v-if="torte.magazzino.fette.length"
              :fette="torte.magazzino.fette"
              :formato="torte.magazzino.formato"
            />
            <p v-else class="hs-nota">Niente da dividere in questo periodo.</p>
          </div>
        </article>
      </div>

      <!-- [sc-48351] §2.3 chiede il trend: due serie sullo stesso grafico,
           perché la domanda vera è quanta parte di quello che si è offerto è
           diventata un ordine, e si legge solo con le barre vicine. -->
      <article class="hs-card hs-card--larga">
        <div class="hs-card__testa">
          <h3><i class="fa-solid fa-chart-column"></i> Andamento</h3>
          <div class="hs-toggle hs-toggle--andamento">
            <button
              type="button"
              :class="{ 'hs-toggle--attivo': misuraGrafico === 'valore' }"
              @click="misuraGrafico = 'valore'"
            >
              Per valore
            </button>
            <button
              type="button"
              :class="{ 'hs-toggle--attivo': misuraGrafico === 'numero' }"
              @click="misuraGrafico = 'numero'"
            >
              Per numero
            </button>
          </div>
        </div>

        <p class="hs-sottotitolo">{{ intervalloLeggibile }}</p>

        <p v-if="graficoVuoto" class="hs-nota">
          Niente da mostrare in questo periodo.
        </p>
        <template v-else>
          <!-- In SVG e non in div alti in percentuale: l'altezza in
               percentuale deve risolversi attraverso tre contenitori
               annidati, e basta che uno non abbia un'altezza definita perché
               le barre non si vedano. Il viewBox non ha questo problema. -->
          <div class="hs-grafico">
            <div class="hs-asse">
              <span>{{ quantoGrafico(massimoGrafico) }}</span>
              <span>{{ quantoGrafico(massimoGrafico / 2) }}</span>
              <span>0</span>
            </div>

            <div class="hs-tela">
              <span class="hs-linea" style="top: 0"></span>
              <span class="hs-linea" style="top: 50%"></span>
              <svg
                class="hs-svg"
                :viewBox="`0 0 ${grafico.length * 10} 100`"
                preserveAspectRatio="none"
                role="img"
                :aria-label="descrizioneGrafico"
              >
                <g v-for="(casella, i) in grafico" :key="casella.casella">
                  <title>{{ casella.titolo }}</title>
                  <rect
                    class="hs-rect hs-rect--preventivi"
                    :x="i * 10 + 1"
                    :y="100 - casella.altezzaPreventivi"
                    width="3.6"
                    :height="casella.altezzaPreventivi"
                  />
                  <rect
                    class="hs-rect hs-rect--ordini"
                    :x="i * 10 + 5.4"
                    :y="100 - casella.altezzaOrdini"
                    width="3.6"
                    :height="casella.altezzaOrdini"
                  />
                </g>
              </svg>
            </div>

            <div class="hs-etichette">
              <span v-for="(casella, i) in grafico" :key="casella.casella">
                {{ etichettaVisibile(i) ? casella.etichetta : "" }}
              </span>
            </div>
          </div>
          <ul class="hs-legenda">
            <li>
              <span class="hs-punto hs-punto--preventivi"></span> Preventivi
            </li>
            <li>
              <span class="hs-punto hs-punto--ordini"></span> Ordini confermati
            </li>
          </ul>
        </template>
      </article>

      <div class="hs-classifiche">
        <article class="hs-card hs-card--larga">
          <div class="hs-card__testa">
            <h3>
              <i class="fa-solid fa-boxes-stacked"></i> Prodotti più venduti
            </h3>
            <p class="hs-sottotitolo">
              Dalle righe degli ordini confermati nel periodo
            </p>
            <div class="hs-toggle hs-toggle--prodotti">
              <button
                type="button"
                :class="{ 'hs-toggle--attivo': ordineProdotti === 'quantita' }"
                @click="ordineProdotti = 'quantita'"
              >
                Per quantità
              </button>
              <button
                type="button"
                :class="{ 'hs-toggle--attivo': ordineProdotti === 'valore' }"
                @click="ordineProdotti = 'valore'"
              >
                Per valore
              </button>
            </div>
          </div>
          <p v-if="prodotti.length === 0" class="hs-nota">
            Nessun ordine confermato in questo periodo.
          </p>
          <table v-else class="hs-tabella">
            <thead>
              <tr>
                <th>Articolo</th>
                <th>Marchio</th>
                <th class="hs-destra">
                  <span
                    class="hs-def"
                    tabindex="0"
                    :title="DEF.quantitaVenduta"
                  >
                    Quantità
                    <i class="fa-regular fa-circle-question"></i>
                  </span>
                </th>
                <th class="hs-destra">
                  <span class="hs-def" tabindex="0" :title="DEF.ordiniProdotto">
                    Ordini
                    <i class="fa-regular fa-circle-question"></i>
                  </span>
                </th>
                <th class="hs-destra">
                  <span class="hs-def" tabindex="0" :title="DEF.valoreVenduto">
                    Valore
                    <i class="fa-regular fa-circle-question"></i>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="riga in prodotti" :key="riga.itemId">
                <td>{{ riga.nome }}</td>
                <td class="hs-muto">{{ riga.marchio || "—" }}</td>
                <td class="hs-destra">
                  {{ riga.quantita }}
                  <span class="hs-muto">{{ riga.unita || "pz" }}</span>
                </td>
                <td class="hs-destra">{{ riga.ordini }}</td>
                <td class="hs-destra">{{ soldi(riga.valore) }}</td>
              </tr>
            </tbody>
          </table>
        </article>

        <article class="hs-card hs-card--larga">
          <div class="hs-card__testa">
            <h3><i class="fa-solid fa-trophy"></i> Clienti migliori</h3>
            <p class="hs-sottotitolo">
              Per valore degli ordini confermati nel periodo
            </p>
          </div>
          <p v-if="dati.clienti.migliori.length === 0" class="hs-nota">
            Nessun ordine confermato in questo periodo.
          </p>
          <table v-else class="hs-tabella">
            <thead>
              <tr>
                <th>Cliente</th>
                <th class="hs-destra">Ordini</th>
                <th class="hs-destra">
                  <span class="hs-def" tabindex="0" :title="DEF.ordiniCliente">
                    Valore
                    <i class="fa-regular fa-circle-question"></i>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="riga in dati.clienti.migliori" :key="riga.clientId">
                <td>{{ riga.nome }}</td>
                <td class="hs-destra">{{ riga.ordini }}</td>
                <td class="hs-destra">{{ soldi(riga.valore) }}</td>
              </tr>
            </tbody>
          </table>
        </article>
      </div>
    </template>
  </section>
</template>

<script>
import PieStat from "@/components/PieStat.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import axiosInstance from "@/axiosInstance";
import { formatPriceToPrint } from "@/utilities/formatting";

/**
 * [sc-48351] I numeri della home: §2.2 e §2.3.
 *
 * Un blocco per widget, come li manda l'API. Aggiungerne uno vuol dire
 * aggiungere una card qui: non c'è nessuna logica da riscrivere, ed è questo
 * che il documento chiede quando dice «estendibile».
 *
 * **I conti non si rifanno.** Valori, medie, percentuali e confronti arrivano
 * calcolati: rifarli qui vorrebbe dire avere due verità sullo stesso numero, e
 * quella sbagliata sarebbe sempre la sola che il cliente guarda.
 */
/**
 * Cosa vuol dire ogni numero, con le stesse parole che userebbe chi lo legge.
 *
 * Stanno qui e non sparse nel markup perché sono la stessa cosa che le query
 * fanno: se una definizione cambia, si cambia la frase accanto alla query e
 * questa riga, e non si va a cercare dove era scritta.
 */
const DEFINIZIONI = Object.freeze({
  medio: "Il valore totale dei preventivi diviso il loro numero.",
  convertiti:
    "Preventivi con data nel periodo che sono stati confermati, in qualunque momento.",
  daConsegnare:
    "Ordini confermati e non ancora consegnati, oggi: non dipendono dal periodo scelto.",
  daIncassare:
    "Quanto resta da incassare sugli ordini non ancora pagati per intero, oggi.",
  giorniPerConvertire:
    "Giorni medi tra la data del preventivo e il giorno in cui è stato confermato, sui preventivi confermati nel periodo.",
  nuoviClienti:
    "Clienti inseriti in anagrafica nel periodo, anche se non hanno ancora chiesto niente.",
  giaClienti:
    "Clienti con un preventivo nel periodo che ne avevano già uno prima che il periodo cominciasse.",
  valoreMedioCliente:
    "Quanto ha comprato in media un cliente da sempre: tutti gli ordini confermati diviso i clienti che hanno comprato almeno una volta. Non cambia col periodo scelto.",
  ogniQuantiGiorni:
    "Giorni medi tra un ordine e il successivo, sui clienti che hanno almeno due ordini confermati. Guarda tutta la loro storia, non solo il periodo.",
  sottoScorta: "Articoli segnati come critici nella scheda di magazzino.",
  quantitaVenduta:
    "Quanto è uscito negli ordini confermati del periodo, nell'unità con cui si vende l'articolo: mq, ml o pezzi. Per la merce a scatole si contano le scatole intere.",
  ordiniProdotto:
    "In quanti ordini confermati del periodo compare l'articolo: dice se la quantità viene da tanti ordini o da una commessa sola.",
  valoreVenduto:
    "IVA compresa e sconti di riga già tolti. Non tiene conto di sconto o maggiorazione applicati all'intero documento.",
  ordiniCliente:
    "Quanti ordini il cliente ha confermato nel periodo, e quanto valgono in tutto: IVA, imballo, sconto e maggiorazione del documento compresi.",
  valoreGiacenza:
    "Prezzo di listino per la giacenza: i mq per le piastrelle, i ml per i profili, le scatole per il resto.",
});

export default {
  name: "HomeStats",
  components: { PieStat, StatusSelect },
  data() {
    return {
      periodo: "mese",
      periodi: ["mese", "trimestre", "anno"],
      ordineProdotti: "quantita",
      /** Quale scheda ha la torta aperta: una alla volta. */
      aperta: "",
      DEF: DEFINIZIONI,
      misuraGrafico: "valore",
      dati: null,
      carico: true,
      errore: "",
    };
  },
  computed: {
    opzioniPeriodo() {
      return this.periodi.map((p) => ({
        value: p,
        label: this.nomePeriodo(p),
      }));
    },
    /**
     * Le colonne del grafico, con le altezze già in percentuale.
     *
     * L'altezza si misura sulla casella più alta del periodo: un asse fisso
     * schiaccerebbe i mesi tranquilli fino a farli sembrare vuoti.
     */
    /**
     * Le torte delle schede: parte su tutto, una per scheda.
     *
     * Gli spicchi vengono dai numeri che la scheda mostra già: la torta non
     * aggiunge dati, dice come sono divisi.
     */
    torte() {
      const preventivi = this.dati?.preventivi || {};
      const ordini = this.dati?.ordini || {};
      const clienti = this.dati?.clienti || {};
      const magazzino = this.dati?.magazzino || {};
      const aperti = Math.max(
        (preventivi.numero || 0) - (preventivi.convertiti || 0),
        0
      );

      return {
        preventivi: {
          nota: "Quanti sono diventati un ordine",
          formato: "numero",
          fette: [
            {
              etichetta: "Diventati ordini",
              valore: preventivi.convertiti || 0,
            },
            { etichetta: "Ancora aperti", valore: aperti },
          ],
        },
        ordini: {
          nota: "Gli ordini da consegnare, per stato",
          formato: "numero",
          fette: (ordini.perStato || []).map((s) => ({
            etichetta: s.stato,
            valore: s.quanti,
          })),
        },
        clienti: {
          nota: "I clienti del periodo: quanti c'erano già e quanti sono alla prima richiesta",
          formato: "numero",
          fette: [
            {
              etichetta: "Già clienti da prima",
              valore: clienti.giaClienti || 0,
            },
            {
              etichetta: "Alla prima richiesta",
              valore: clienti.allaPrimaRichiesta || 0,
            },
          ],
        },
        magazzino: {
          nota: "Il valore della giacenza, per categoria",
          formato: "soldi",
          fette: (magazzino.perCategoria || []).map((c) => ({
            etichetta: c.etichetta,
            valore: c.valore,
          })),
        },
      };
    },
    /**
     * Con cosa si confrontano le variazioni.
     *
     * Il periodo in corso si misura sullo **stesso tratto** di quello prima —
     * il 5 di ottobre, cinque giorni contro cinque giorni — e questo va detto:
     * un «−100%» il 2 del mese, letto come «contro tutto il mese scorso»,
     * sembra un crollo e invece è il mese appena cominciato.
     */
    confrontoCon() {
      const prima = this.dati?.periodo?.precedente;
      if (!prima?.da || !prima?.a) return "";
      const da = new Date(prima.da);
      const a = new Date(prima.a);
      // L'estremo è il momento in cui il tratto si chiude: il giorno da
      // scrivere è quello, non il successivo.
      const stessoAnno = da.getUTCFullYear() === a.getUTCFullYear();
      const stessoMese = stessoAnno && da.getUTCMonth() === a.getUTCMonth();
      const giorno = (d, opzioni) =>
        d.toLocaleDateString("it-IT", { timeZone: "UTC", ...opzioni });

      if (stessoMese) {
        return `${da.getUTCDate()} – ${giorno(a, {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}`;
      }
      return `${giorno(da, { day: "numeric", month: "long" })} – ${giorno(a, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}`;
    },
    /** La casella più alta del periodo: è lei a dare la scala. */
    massimoGrafico() {
      const misura = this.misuraGrafico;
      return (this.dati?.andamento || []).reduce(
        (piu, c) => Math.max(piu, c.preventivi[misura], c.ordini[misura]),
        0
      );
    },
    /**
     * Di che cosa sono le caselle: senza, «1, 6, 11» sotto le barre sono
     * numeri e basta. Le date le formatta il browser, che sa l'italiano.
     */
    intervalloLeggibile() {
      const da = this.dati?.periodo?.da ? new Date(this.dati.periodo.da) : null;
      if (!da) return "";
      const nome = this.dati.periodo.nome;
      if (nome === "anno") {
        return `${da.getUTCFullYear()}, mese per mese`;
      }
      if (nome === "trimestre") {
        const a = new Date(this.dati.periodo.a);
        a.setUTCDate(a.getUTCDate() - 1);
        return `${this.mese(da)} – ${this.mese(
          a
        )} ${a.getUTCFullYear()}, mese per mese`;
      }
      const primoMaiuscolo = this.mese(da);
      return `${primoMaiuscolo.charAt(0).toUpperCase()}${primoMaiuscolo.slice(
        1
      )} ${da.getUTCFullYear()}, giorno per giorno`;
    },
    grafico() {
      const caselle = this.dati?.andamento || [];
      const misura = this.misuraGrafico;
      const massimo = this.massimoGrafico;
      const altezza = (valore) =>
        massimo > 0
          ? Math.max((valore / massimo) * 100, valore > 0 ? 4 : 0)
          : 0;
      const quanto = (valore) =>
        misura === "valore" ? this.soldi(valore) : valore;

      return caselle.map((c) => ({
        ...c,
        altezzaPreventivi: altezza(c.preventivi[misura]),
        altezzaOrdini: altezza(c.ordini[misura]),
        titolo:
          `${c.etichetta}: ${quanto(c.preventivi[misura])} preventivati, ` +
          `${quanto(c.ordini[misura])} confermati`,
      }));
    },
    /** Quello che il grafico dice, per chi non lo vede. */
    descrizioneGrafico() {
      const misura = this.misuraGrafico === "valore" ? "valore" : "numero";
      return `Andamento ${this.nomePeriodo(
        this.periodo
      ).toLowerCase()}: preventivi e ordini confermati, per ${misura}`;
    },
    graficoVuoto() {
      return this.grafico.every(
        (c) => c.altezzaPreventivi === 0 && c.altezzaOrdini === 0
      );
    },
    prodotti() {
      if (!this.dati) return [];
      return this.ordineProdotti === "valore"
        ? this.dati.prodotti.perValore
        : this.dati.prodotti.perQuantita;
    },
  },
  mounted() {
    this.leggi();
  },
  methods: {
    soldi: formatPriceToPrint,
    /**
     * Un numero di giorni in parole.
     *
     * Il trattino quando l'API manda `null`: non c'era niente da mediare, e
     * «0 giorni» si leggerebbe come «succede in giornata».
     */
    quantiGiorni(valore) {
      if (valore === null || valore === undefined) return "—";
      const numero = Number(valore);
      if (!Number.isFinite(numero)) return "—";
      if (numero < 1) return "meno di un giorno";
      return `${numero.toLocaleString("it-IT")} ${
        numero === 1 ? "giorno" : "giorni"
      }`;
    },
    /** Apre la torta di una scheda, o la chiude se era già quella aperta. */
    apri(quale) {
      this.aperta = this.aperta === quale ? "" : quale;
    },
    mese(data) {
      return data.toLocaleDateString("it-IT", {
        month: "long",
        timeZone: "UTC",
      });
    },
    /** I numeri dell'asse: euro quando si guarda il valore, conti quando no. */
    quantoGrafico(valore) {
      if (this.misuraGrafico !== "valore") return Math.round(valore);
      return this.soldi(Math.round(valore));
    },
    /**
     * Con trenta giorni le etichette si accavallano: se ne scrive una ogni
     * cinque, e le altre restano nel titolo della colonna.
     */
    etichettaVisibile(indice) {
      const quante = this.grafico.length;
      if (quante <= 12) return true;
      return indice % 5 === 0;
    },
    /**
     * La tendina della piattaforma riemette anche la voce già scelta, il
     * `<select>` no: si rilegge solo se il periodo cambia davvero.
     */
    scegliPeriodo(valore) {
      if (valore === this.periodo) return;
      this.periodo = valore;
      this.leggi();
    },
    nomePeriodo(p) {
      return (
        {
          mese: "Questo mese",
          trimestre: "Questo trimestre",
          anno: "Quest'anno",
        }[p] || p
      );
    },
    /** «+12,4%» o «−3%»: il verso si legge dal segno, non dal colore. */
    delta(valore) {
      const segno = valore > 0 ? "+" : valore < 0 ? "−" : "";
      return `${segno}${Math.abs(valore).toLocaleString("it-IT")}%`;
    },
    versoDi(valore) {
      if (valore > 0) return "hs-delta--su";
      if (valore < 0) return "hs-delta--giu";
      return "";
    },
    async leggi() {
      this.carico = true;
      this.errore = "";
      try {
        const { data } = await axiosInstance.get("statistics/home", {
          params: { periodo: this.periodo },
        });
        this.dati = data;
        if (
          Array.isArray(data?.periodiPossibili) &&
          data.periodiPossibili.length
        ) {
          this.periodi = data.periodiPossibili;
        }
      } catch (err) {
        this.errore =
          err?.response?.data?.message || "Non riesco a leggere i numeri.";
      } finally {
        this.carico = false;
      }
    },
  },
};
</script>

<style scoped>
.hs {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Senza titolo la riga porta la frase del confronto a sinistra e la tendina a
   destra. La tendina si tiene il suo posto con `margin-left: auto`: quando il
   confronto non c'è — periodo senza precedente — resta comunque a destra. */
.hs-testa {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.hs-periodo {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.hs-periodo__label {
  font-size: 13px;
  color: var(--color-text-muted, #71717a);
}

.hs-griglia {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
}

.hs-classifiche {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.hs-card {
  min-width: 0;
  padding: 16px 18px;
  /* [sc-48951] Si stacca con l'ombra: il bordo sopra l'ombra non divide niente. */
  border: none;
  border-radius: 12px;
  background: var(--color-surface, #fff);
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.08));
  box-sizing: border-box;
}

.hs-card h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-muted, #71717a);
}

.hs-card h3 i {
  color: var(--color-primary, #71717a);
}

/* La scheda si apre col clic: lo dice la freccia, e il puntatore lo conferma. */
.hs-card--apribile {
  cursor: pointer;
}

.hs-card--apribile:hover,
.hs-card--apribile:focus-visible {
  border-color: var(--color-primary, #18181b);
}

.hs-freccia {
  margin-left: auto;
  font-size: 11px;
  transition: transform 0.2s ease;
}

.hs-card--aperta .hs-freccia {
  transform: rotate(180deg);
}

.hs-torta {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 0.5px solid var(--color-border, #d4d4d8);
  cursor: default;
}

.hs-torta__nota {
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--color-text-muted, #71717a);
}

.hs-card__testa {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.hs-numero {
  margin: 6px 0 2px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--color-text, #18181b);
}

.hs-sotto {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--color-text-muted, #71717a);
}

.hs-delta {
  margin-left: 6px;
  font-weight: 600;
}

.hs-delta--su {
  color: #15803d;
}

.hs-delta--giu {
  color: #b91c1c;
}

.hs-righe {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}

.hs-righe > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.hs-righe dt,
.hs-righe dd {
  margin: 0;
}

.hs-righe dt {
  color: var(--color-text-muted, #71717a);
}

/* Il numero è quello che si cerca: pesa più dell'etichetta che lo nomina. */
.hs-righe dd {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text, #18181b);
}

/* Quanta parte dei preventivi è diventata un ordine, senza leggere. */
.hs-conversione {
  margin-top: 10px;
  height: 6px;
  border-radius: 999px;
  background: var(--color-surface-alt, #f0f0f1);
  overflow: hidden;
}

.hs-conversione span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--color-primary, #18181b);
}

/* Gli stati degli ordini: pastiglie, non righe di elenco. Sono etichette
   corte e sono tre al massimo. */
.hs-pastiglie {
  margin: 0 0 10px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.hs-pastiglie li {
  padding: 3px 10px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 999px;
  background: var(--color-surface-alt, #f0f0f1);
  font-size: 12px;
}

.hs-pastiglie strong {
  font-variant-numeric: tabular-nums;
}

/* Il grafico: due rettangoli per casella dentro un viewBox, niente libreria
   e niente altezze in percentuale da far risolvere al browser. */
/* Tre colonne di fatto: l'asse a sinistra, la tela a destra, e sotto le
   etichette allineate alla tela. */
.hs-grafico {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    "asse tela"
    ".    etichette";
  gap: 4px 10px;
  margin-top: 8px;
}

.hs-asse {
  grid-area: asse;
  height: 170px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted, #71717a);
}

/* L'ultima cifra dell'asse sta sulla riga di base, non sotto. */
.hs-asse span:last-child {
  transform: translateY(50%);
}

.hs-tela {
  grid-area: tela;
  position: relative;
}

/* Due righe orizzontali: senza, l'altezza delle barre non si può stimare. */
.hs-linea {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 0.5px dashed var(--color-border, #d4d4d8);
  opacity: 0.7;
}

.hs-etichette {
  grid-area: etichette;
}

.hs-svg {
  display: block;
  width: 100%;
  height: 170px;
  border-bottom: 0.5px solid var(--color-border, #d4d4d8);
}

.hs-confronto {
  margin: 0;
  font-size: 12px;
  color: var(--color-text-muted, #71717a);
}

.hs-confronto strong {
  color: var(--color-text, #18181b);
  font-weight: 600;
}

.hs-sottotitolo {
  margin: 0 0 4px;
  font-size: 12px;
  color: var(--color-text-muted, #71717a);
}

.hs-rect--preventivi {
  fill: var(--color-border, #d4d4d8);
}

.hs-rect--ordini {
  fill: var(--color-primary, #18181b);
}

.hs-etichette {
  display: flex;
}

.hs-etichette span {
  flex: 1 1 0;
  min-width: 0;
  font-size: 11px;
  text-align: center;
  color: var(--color-text-muted, #71717a);
}

.hs-legenda {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: var(--color-text-muted, #71717a);
}

.hs-legenda li {
  display: flex;
  align-items: center;
  gap: 6px;
}

.hs-punto {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.hs-punto--preventivi {
  background: var(--color-border, #d4d4d8);
}

.hs-punto--ordini {
  background: var(--color-primary, #18181b);
}

/* Una definizione si chiede passandoci sopra: il punto interrogativo lo dice,
   e il tratteggio sotto l'etichetta lo conferma. */
.hs-def {
  border-bottom: 0.5px dotted var(--color-border, #d4d4d8);
  cursor: help;
}

.hs-def i {
  margin-left: 2px;
  font-size: 10px;
  opacity: 0.7;
}

.hs-etichetta-gruppo {
  margin: 0 0 6px;
  font-size: 12px;
  color: var(--color-text-muted, #71717a);
}

.hs-riga--forte {
  padding-top: 6px;
  border-top: 0.5px solid var(--color-border, #d4d4d8);
  font-weight: 600;
}

.hs-riga--allarme dd {
  color: #b91c1c;
  font-weight: 700;
}

.hs-toggle {
  display: flex;
  gap: 4px;
}

.hs-toggle button {
  padding: 4px 10px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 999px;
  background: transparent;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.hs-toggle--attivo {
  border-color: var(--color-primary, #18181b);
  color: var(--color-primary, #18181b);
  font-weight: 600;
}

.hs-tabella {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.hs-tabella th {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 0.5px solid var(--color-border, #d4d4d8);
  color: var(--color-text-muted, #71717a);
  font-weight: 600;
}

.hs-tabella td {
  padding: 6px 8px;
  border-bottom: 0.5px solid var(--color-surface-alt, #f0f0f1);
}

.hs-destra {
  text-align: right;
}

.hs-muto {
  color: var(--color-text-muted, #71717a);
}

.hs-nota,
.hs-errore {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted, #71717a);
}

.hs-errore {
  color: #b91c1c;
}

@media (max-width: 640px) {
  .hs-numero {
    font-size: 26px;
  }
}
</style>
